import { tariffIsShared } from "@Logic/PredictInstant/AvgDataUsageSplits";
import BaseEntity from "@Shared/Entities/AbstractEntities/BaseEntity";
import { Network } from "@Shared/Entities/Customer/Usage/enums";
import { DeviceCostLevels, FundingOptions, Platform, } from "@Shared/Entities/Supplier/InstantPackage/enums";
import { ValidationException } from "@Shared/Exceptions";
import constraints from "./constraints";
import migrate from "./migrate";
export var Status;
(function (Status) {
    Status["Contacted"] = "contacted";
    Status["ContractPending"] = "contractPending";
    Status["ContractComplete"] = "contractComplete";
})(Status || (Status = {}));
export default class GeneratedPackage extends BaseEntity {
    constructor(data = {}) {
        super(migrate(data), constraints);
    }
    get data() {
        const { instantPackageId, customerId, supplierId, customerUserId, supplierUserId, parameters, lines, contractLength, monthlyLinesCost, monthlyDeviceCost, monthlyDeviceCostPerLine, monthlyTotalCost, totalContractCost, customerService, simChange, packageIsLocked, scores, commonCharges, status, monthlyLinesCostWithVAT, monthlyDeviceCostWithVAT, monthlyCostPerLine, monthlyCostPerLineWithVAT, monthlyDeviceCostPerLineWithVAT, totalContractCostWithVAT, monthlyTotalCostWithVAT, tariffWithoutPhonesPerLinePerMonth, tariffWithoutPhonesPerLinePerMonthWithVAT, tariffWithPhonesPerLinePerMonth, tariffWithPhonesPerLinePerMonthWithVAT, customerName, } = this;
        const { version } = this._data;
        return {
            version,
            instantPackageId,
            customerId,
            supplierId,
            customerUserId,
            supplierUserId,
            parameters,
            lines,
            contractLength,
            monthlyLinesCost,
            monthlyDeviceCost,
            monthlyDeviceCostPerLine,
            monthlyTotalCost,
            totalContractCost,
            customerService,
            simChange,
            packageIsLocked,
            scores,
            commonCharges,
            status,
            monthlyLinesCostWithVAT,
            monthlyDeviceCostWithVAT,
            monthlyCostPerLine,
            monthlyCostPerLineWithVAT,
            monthlyDeviceCostPerLineWithVAT,
            totalContractCostWithVAT,
            monthlyTotalCostWithVAT,
            tariffWithoutPhonesPerLinePerMonth,
            tariffWithoutPhonesPerLinePerMonthWithVAT,
            tariffWithPhonesPerLinePerMonth,
            tariffWithPhonesPerLinePerMonthWithVAT,
            customerName,
        };
    }
    get links() {
        return {
            self: `${this.baseUrl}`,
        };
    }
    get instantPackageId() {
        return this._data.instantPackageId;
    }
    get customerId() {
        return this._data.customerId;
    }
    get supplierId() {
        return this._data.supplierId;
    }
    get customerUserId() {
        return this._data.customerUserId;
    }
    get supplierUserId() {
        return this._data.supplierUserId;
    }
    get vatRate() {
        return 1.2;
    }
    get platform() {
        return this.parameters.platform;
    }
    get parameters() {
        return this._data.parameters;
    }
    get lines() {
        if (this.packageIsLocked) {
            return this._data.lines;
        }
        switch (tariffIsShared(this.parameters.tariffOption)) {
            case true:
                return this.getSharedLineDetails();
            case false:
                return this.getIndividualLineDetails();
        }
    }
    getSharedLineDetails() {
        const requiredLines = this.parameters.lines;
        const { totalRequiredData } = this.parameters.dataDetails;
        const dataPerLine = totalRequiredData / requiredLines;
        const tariffLevelKey = Object.entries(this.parameters.tariffs[this.parameters.tariffOption].lengths[`length${this.parameters.contractLength}`].levels)
            .filter(([key, { costPerLine = 0 }]) => (!Number.isNaN(Number(key)) || key === "Unlimited") &&
            costPerLine > 0)
            .map(([key]) => key)
            .map((key) => (key === "Unlimited" ? key : Number(key)))
            .sort((a, b) => a === "Unlimited" ? 1 : b === "Unlimited" ? -1 : a - b)
            .find((levelNumber) => {
            if (typeof levelNumber === "number" &&
                dataPerLine <= levelNumber) {
                return true;
            }
            if (levelNumber === "Unlimited") {
                return true;
            }
        });
        const tariffDetails = this.parameters.tariffs[this.parameters.tariffOption].lengths[`length${this.parameters.contractLength}`].levels[tariffLevelKey];
        const lines = [];
        for (let index = 0; index < requiredLines; index++) {
            const cost = tariffDetails.costPerLine - tariffDetails.discount || 0;
            lines.push({
                allowances: {
                    data: {
                        type: this.parameters.tariffOption,
                        value: tariffLevelKey,
                    },
                    callLimit: tariffDetails.callAllowance,
                    smsLimit: tariffDetails.smsAllowance,
                },
                cost,
                costWithVAT: cost * this.vatRate,
            });
        }
        return lines;
    }
    getIndividualLineDetails() {
        const { lineUsageSplit } = this.parameters.dataDetails;
        const lines = [];
        for (let index = 0; index < lineUsageSplit.low; index++) {
            lines.push(this.addIndividualLine(lineUsageSplit.dataLevels.low));
        }
        for (let index = 0; index < lineUsageSplit.mid; index++) {
            lines.push(this.addIndividualLine(lineUsageSplit.dataLevels.mid));
        }
        for (let index = 0; index < lineUsageSplit.high; index++) {
            lines.push(this.addIndividualLine(lineUsageSplit.dataLevels.high));
        }
        for (let index = 0; index < (lineUsageSplit.unlimited || 0); index++) {
            lines.push(this.addIndividualLine(Number.POSITIVE_INFINITY));
        }
        return lines;
    }
    addIndividualLine(dataAllowance) {
        // find the tariff option that is equal to or just above the data allowance for this line
        const listOfKeys = Object.entries(this.parameters.tariffs[this.parameters.tariffOption].lengths[`length${this.parameters.contractLength}`].levels);
        const filteredList = listOfKeys.filter(([key, { costPerLine = 0 }]) => (!Number.isNaN(Number(key)) || key === "Unlimited") &&
            costPerLine > 0);
        const mappedList = filteredList.map(([key]) => (key === "Unlimited" ? key : Number(key)));
        const sortedList = mappedList.sort((a, b) => a === "Unlimited" ? 1 : b === "Unlimited" ? -1 : a - b);
        const tariffLevelKey = sortedList.find((levelNumber) => {
            if (typeof levelNumber === "number" &&
                dataAllowance <= levelNumber) {
                return true;
            }
            if (levelNumber === "Unlimited") {
                return true;
            }
        });
        const tariffDetails = this.parameters.tariffs[this.parameters.tariffOption].lengths[`length${this.parameters.contractLength}`].levels[tariffLevelKey];
        const cost = tariffDetails.costPerLine - tariffDetails.discount || 0;
        return {
            allowances: {
                data: {
                    type: this.parameters.tariffOption,
                    value: tariffLevelKey,
                },
                callLimit: tariffDetails.callAllowance,
                smsLimit: tariffDetails.smsAllowance,
            },
            cost,
            costWithVAT: cost * this.vatRate,
        };
    }
    get contractLength() {
        if (this.packageIsLocked) {
            return this._data.contractLength;
        }
        return this.parameters.contractLength;
    }
    get monthlyLinesCost() {
        return this.lines.reduce((monthlyLineTotal, line) => monthlyLineTotal + line.cost, 0);
    }
    get monthlyLinesCostWithVAT() {
        return this.monthlyLinesCost * this.vatRate;
    }
    get monthlyDeviceCost() {
        return this._data.monthlyDeviceCost ?? 0;
    }
    set monthlyDeviceCost(cost) {
        this._data.monthlyDeviceCost = cost;
    }
    getMonthlyDeviceCost(fundOption) {
        switch (fundOption) {
            case FundingOptions.Leasing:
                return this.getLeasingCost();
            case FundingOptions.HardwareFund:
                return this.getHardwareFundCost();
            default:
                return 0;
        }
    }
    get monthlyDeviceCostWithVAT() {
        return this.monthlyDeviceCost * this.vatRate;
    }
    getLeasingCost() {
        const leasingDetails = this.parameters.leasing[`length${this.parameters.contractLength}`];
        const phoneLevelSplit = JSON.parse(this.parameters.phoneLevelSplit);
        const phoneValueRequired = phoneLevelSplit
            ? phoneLevelSplit.low * DeviceCostLevels.Low +
                phoneLevelSplit.mid * DeviceCostLevels.Mid +
                phoneLevelSplit.high * DeviceCostLevels.High
            : 0;
        const totalLeasingCost = phoneValueRequired *
            (1 + leasingDetails.additionalPercentage / 100) +
            leasingDetails.initialSignupCost +
            leasingDetails.finalPayment;
        return totalLeasingCost / this.parameters.contractLength;
    }
    getHardwareFundCost() {
        const hardwareFundDetails = this.parameters.hardwareFunds[this.parameters.tariffOption][`length${this.parameters.contractLength}`];
        if (hardwareFundDetails === undefined) {
            throw new ValidationException("No hardware fund costs found");
        }
        const phoneSplit = JSON.parse(this.parameters.phoneLevelSplit);
        const allowanceSplit = this.lines.reduce((splitObject, { allowances }) => {
            const allowanceValue = allowances.data.value;
            if (splitObject[allowanceValue]) {
                splitObject[allowanceValue] += 1;
            }
            else {
                splitObject[allowanceValue] = 1;
            }
            return splitObject;
        }, {});
        return this.getCheapestTotalHardwareFundCost(hardwareFundDetails, phoneSplit, allowanceSplit);
    }
    get totalHardwareValue() {
        const { parameters: { phoneLevelSplit }, } = this;
        if (this.parameters.deviceFundOptions !== FundingOptions.HardwareFund) {
            return 0;
        }
        return (phoneLevelSplit.low * DeviceCostLevels.Low +
            phoneLevelSplit.mid * DeviceCostLevels.Mid +
            phoneLevelSplit.high * DeviceCostLevels.High);
    }
    getCheapestTotalHardwareFundCost(costs, // hardware fund cost at each allowance level, e.g. { 2GB: { low: £10, mid: £15, high: £20 }, 5GB: { low: £12, mid: £18, high: £25 } }
    devices, // number of devices at each device level, e.g. { low: 2 devices, mid: 1 device, high: 2 devices }
    allowances // number of lines on each allowance level, e.g. { 2GB: 3 lines, 5GB: 2 lines }
    ) {
        const totalDevices = devices.low + devices.mid + devices.high;
        const totalAllowances = Object.values(allowances).reduce((sum, count) => sum + count, 0);
        if (totalDevices > totalAllowances) {
            throw new ValidationException("More devices than allowances, cannot assign hardware fund costs");
        }
        const levels = Object.keys(allowances)
            .map((key) => (key === "Unlimited" ? key : Number(key)))
            .filter((level) => allowances[level] > 0)
            .sort((a, b) => a === "Unlimited" ? 1 : b === "Unlimited" ? -1 : a - b);
        let dp = new Map();
        const makeKey = (low, mid, high) => `${low},${mid},${high}`;
        dp.set(makeKey(0, 0, 0), 0);
        for (const level of levels) {
            const capacity = allowances[level];
            const levelCosts = costs[level];
            const nextDp = new Map();
            for (const [key, currentCost] of dp.entries()) {
                const [assignedLow, assignedMid, assignedHigh] = key
                    .split(",")
                    .map(Number);
                const remainingLow = devices.low - assignedLow;
                const remainingMid = devices.mid - assignedMid;
                const remainingHigh = devices.high - assignedHigh;
                // Try every way of assigning devices to this allowance level
                // without exceeding number of lines remaining at this level
                for (let useLow = 0; useLow <= remainingLow; useLow++) {
                    for (let useMid = 0; useMid <= remainingMid; useMid++) {
                        for (let useHigh = 0; useHigh <= remainingHigh; useHigh++) {
                            const used = useLow + useMid + useHigh;
                            if (used > capacity) {
                                continue;
                            }
                            // Skip if any assigned device type has a cost of 0 or undefined for this allowance level
                            if ((useLow > 0 &&
                                (!levelCosts ||
                                    !levelCosts.low ||
                                    levelCosts.low === 0)) ||
                                (useMid > 0 &&
                                    (!levelCosts ||
                                        !levelCosts.mid ||
                                        levelCosts.mid === 0)) ||
                                (useHigh > 0 &&
                                    (!levelCosts ||
                                        !levelCosts.high ||
                                        levelCosts.high === 0))) {
                                continue;
                            }
                            const newLow = assignedLow + useLow;
                            const newMid = assignedMid + useMid;
                            const newHigh = assignedHigh + useHigh;
                            const extraCost = useLow * levelCosts.low +
                                useMid * levelCosts.mid +
                                useHigh * levelCosts.high;
                            const newKey = makeKey(newLow, newMid, newHigh);
                            const newTotalCost = currentCost + extraCost;
                            const existingDp = nextDp.get(newKey);
                            if (existingDp === undefined ||
                                newTotalCost < existingDp) {
                                nextDp.set(newKey, newTotalCost);
                            }
                        }
                    }
                }
            }
            dp = nextDp;
        }
        const finalKey = makeKey(devices.low, devices.mid, devices.high);
        const result = dp.get(finalKey);
        if (result === undefined) {
            throw new ValidationException("Could not find a valid assignment of devices to allowance levels");
        }
        return result;
    }
    get monthlyDeviceCostPerLine() {
        const { monthlyDeviceCost, parameters } = this;
        return monthlyDeviceCost / parameters.lines;
    }
    get monthlyDeviceCostPerLineWithVAT() {
        return this.monthlyDeviceCostPerLine * this.vatRate;
    }
    get monthlyCostPerLine() {
        const { monthlyTotalCost, parameters } = this;
        return monthlyTotalCost / parameters.lines;
    }
    get monthlyCostPerLineWithVAT() {
        return this.monthlyCostPerLine * this.vatRate;
    }
    // cost getters for display in card
    get tariffWithoutPhonesPerLinePerMonth() {
        return this.monthlyCostPerLine - this.monthlyDeviceCostPerLine;
    }
    get tariffWithoutPhonesPerLinePerMonthWithVAT() {
        return this.tariffWithoutPhonesPerLinePerMonth * this.vatRate;
    }
    get tariffWithPhonesPerLinePerMonth() {
        return this.monthlyCostPerLine;
    }
    get tariffWithPhonesPerLinePerMonthWithVAT() {
        return this.tariffWithPhonesPerLinePerMonth * this.vatRate;
    }
    get monthlyTotalCost() {
        const { monthlyLinesCost, monthlyDeviceCost, parameters } = this;
        // add other components of the total cost here
        return monthlyLinesCost + monthlyDeviceCost;
    }
    get monthlyTotalCostWithVAT() {
        return this.monthlyTotalCost * this.vatRate;
    }
    get totalContractCost() {
        const { monthlyTotalCost, contractLength } = this;
        return monthlyTotalCost * contractLength;
    }
    get totalContractCostWithVAT() {
        return this.totalContractCost * this.vatRate;
    }
    get customerService() {
        if (this.packageIsLocked) {
            return this._data.customerService;
        }
        return this.parameters.customerService;
    }
    get roaming() {
        if (this.packageIsLocked) {
            return this._data.roaming;
        }
        return this.parameters.roaming;
    }
    get commonCharges() {
        if (this.packageIsLocked) {
            return this._data.commonCharges;
        }
        return this.parameters.commonCharges;
    }
    get simChange() {
        if (this.packageIsLocked) {
            return this._data.simChange;
        }
        const convertPlatformToNetwork = () => {
            switch (this.parameters.platform) {
                case Platform.EE:
                case Platform.EEPlan:
                    return Network.EE;
                case Platform.O2:
                case Platform.O2Plan:
                    return Network.O2;
                case Platform.Vodafone:
                    return Network.Vodafone;
                case Platform.Three:
                    return Network.Three;
            }
        };
        if (this.parameters.network === convertPlatformToNetwork()) {
            return "Maybe";
        }
        return "Yes";
    }
    get packageIsLocked() {
        return this._data.packageIsLocked || false;
    }
    get scores() {
        return this._data.scores;
    }
    get status() {
        return this._data.status;
    }
    get customerName() {
        return this._data.customerName;
    }
}
